// Foundations
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  ElementRef,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
  FormControl,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

// rxjs
import { startWith, map, debounce, finalize } from 'rxjs/operators';
import { interval, Observable } from 'rxjs';

// Models
import { BinaryChoice } from '@app/models/binary-choice.model';

// Components
import { ConfirmDialogComponent } from '@app/components/_share/confirm-dialog/confirm-dialog.component';

// Service
import { AnalyticsService } from '@app/services/analytics.service';
import {
  Breed,
  Color,
  Gender,
  GetBreedsGQL,
  GetColorsGQL,
  Pet,
  PetBreed,
  PetColor,
  UpdatePetGQL,
} from '@app/services/graphql.service';
import { UnitConversionPipe } from '@app/pipes/unit-conversion.pipe';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';

// Helper function
import { cloneAbstractControl } from '@app/shared/clone-abstract-control';

@Component({
  selector: 'app-camper-details',
  templateUrl: './camper-details.component.html',
  styleUrls: ['./camper-details.component.scss'],
})
export class CamperDetailsComponent implements OnInit {
  @Input() petId: number;
  @Output() petNameChange = new EventEmitter<string>();
  @Output() dogRemoved = new EventEmitter();

  form: FormGroup;
  formClone: FormGroup;

  editMode = false;
  isDobTouched = false;
  minDate: Date;
  maxDate: Date;
  breedList: Breed[];
  colorList: Color[];
  wholeBreedList: Breed[];
  wholeColorList: Color[];
  genderList: BinaryChoice[] = [
    { text: 'Male', value: Gender.M },
    { text: 'Female', value: Gender.F },
  ];
  // each dropdown has its own filtered list
  filteredBreeds: Observable<Breed[]>[] = [];
  filteredColors: Observable<Color[]>[] = [];
  showWarning = false;
  yesNoList: BinaryChoice[] = [
    { text: 'Yes', value: true },
    { text: 'No', value: false },
  ];

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private _analytics: AnalyticsService,
    private getBreedsQuery: GetBreedsGQL,
    private getColorsQuery: GetColorsGQL,
    private updatePetGQL: UpdatePetGQL,
    private convertUnit: UnitConversionPipe,
    private loadingIndicatorService: LoadingIndicatorService,
  ) {
    this.form = this.fb.group({
      // <!-- CBW-784 Names -->
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      dob: ['', [Validators.required]],
      breedArr: this.fb.array([]),
      colorArr: this.fb.array([]),
      weight: [null, [Validators.required]],
      gender: ['', [Validators.required]],
      spayNeutered: [''],
    });

    // initialize date range
    const dateFourMonthsAgo = new Date();
    dateFourMonthsAgo.setMonth(dateFourMonthsAgo.getMonth() - 4);
    this.minDate = new Date('January 1, 1970');
    this.maxDate = dateFourMonthsAgo;
  }

  ngOnInit(): void {
    this.getBreedsQuery.fetch().subscribe((result) => {
      this.breedList = result.data.getBreeds;
      this.wholeBreedList = result.data.getBreeds;
    });
    this.getColorsQuery.fetch().subscribe((result) => {
      this.colorList = result.data.getColors;
      this.wholeColorList = result.data.getColors;
    });
    this.form.valueChanges.subscribe((formValues) => {
      const existBreedArr = formValues['breedArr'].map((value) =>
        value?.label?.toLowerCase()
      );
      this.breedList = this.wholeBreedList?.filter(
        (option) => !existBreedArr.includes(option.label.toLowerCase())
      );
      const existColorArr = formValues['colorArr'].map((value) =>
        value?.label?.toLowerCase()
      );
      this.colorList = this.wholeColorList?.filter(
        (option) => !existColorArr.includes(option.label.toLowerCase())
      );
    });
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f(): any {
    return this.form.controls;
  }
  // <!-- CBW-784 Names -->

  populateForms(pet: Partial<Pet>) {
    const {
      firstName,
      lastName,
      nickName,
      dateOfBirth,
      weight,
      breeds,
      colors,
      gender,
      spayNeutered,
    } = pet;
    this.form.patchValue({
      firstName: firstName ? firstName : nickName,
      lastName,
      dob: dateOfBirth,
      weight: weight
        ? +this.convertUnit.transform(weight?.weight, 'g', 'lb')
        : '',
      gender: gender !== Gender.U ? gender : '',
      spayNeutered,
    });
    // if we have data for any of these disable the form and show an error
    if (pet.firstName) {
      this.showWarning = true;
      this.form.controls['firstName'].disable();
    }
    if (pet.lastName) {
      this.showWarning = true;
      this.form.controls['lastName'].disable();
    }
    if (pet.dateOfBirth) {
      this.showWarning = true;
      this.form.controls['dob'].disable();
    }
    if (pet.weight) {
      this.showWarning = true;
      this.form.controls['weight'].disable();
    }
    this.populateBreeds(breeds);
    this.populateColors(colors);
    this.editMode = this.form.invalid;
    this.formClone = cloneAbstractControl(this.form);
  }

  resetFormToDefault() {
    this.form = cloneAbstractControl(this.formClone);
  }

  onSave(): void {
    this._analytics.logButtonTap('dogProfile_saveStepCamperDetails');

    this.updateData();
  }

  // dob date picker

  setDobTouched(): void {
    this.isDobTouched = true;
  }

  // breeds

  get breedFormArray(): FormArray {
    return this.form.get('breedArr') as FormArray;
  }

  createBreed(): FormControl {
    return this.fb.control(null, Validators.required);
  }

  addBreed(): void {
    this._analytics.logLinkTap('dogProfile_addBreed');

    this.breedFormArray.push(this.createBreed());
    this._manageFilteredBreedsByFormArrayIndex(this.breedFormArray.length - 1);
  }

  removeBreed(index): void {
    this.breedFormArray.removeAt(index);
    this.filteredBreeds.splice(index, 1);
    this.filterBreedsOption();
  }

  getBreedLabel(breed: Breed): string {
    if (breed) {
      return breed.label;
    }
  }

  getYesNoText(yesNoValue: boolean) {
    const res = this.yesNoList.find((g) => g.value === yesNoValue);
    return res ? res.text : '';
  }

  blurBreed(index: number) {
    const control = this.breedFormArray.at(index);
    if (typeof control.value === 'string') {
      this.breedFormArray.at(index).patchValue(null);
    }
  }

  // colors

  get colorFormArray(): FormArray {
    return this.form.get('colorArr') as FormArray;
  }

  createColor(): FormControl {
    return this.fb.control(null, Validators.required);
  }

  addColor(): void {
    this._analytics.logLinkTap('dogProfile_addColor');

    this.colorFormArray.push(this.createColor());
    this._manageFilteredColorsByFormArrayIndex(this.colorFormArray.length - 1);
  }

  removeColor(index): void {
    this.colorFormArray.removeAt(index);
    this.filteredColors.splice(index, 1);
    this.filterColorOption();
  }

  getColorLabel(color: Color): string {
    if (color) {
      return color.label;
    }
  }

  blurColor(index: number) {
    const control = this.colorFormArray.at(index);
    if (typeof control.value === 'string') {
      this.colorFormArray.at(index).patchValue(null);
    }
  }

  // weight

  blurWeight() {
    const weightCtrl = this.form.get('weight');

    if (typeof weightCtrl.value !== 'number' || weightCtrl.value === 0) {
      weightCtrl.patchValue(null);
    } else if (weightCtrl.value < 0) {
      weightCtrl.patchValue(weightCtrl.value * -1);
    } else {
      weightCtrl.patchValue(parseFloat('' + weightCtrl.value));
    }
  }

  // gender

  get genderText(): string {
    const res = this.genderList.find(
      (g) => g.value === this.form.controls.gender.value
    );
    return res ? res.text : '';
  }

  setGender(genderValue: string): void {
    this.form.controls.gender.setValue(genderValue ? genderValue : '');
  }

  // remove dog dialog

  showRemoveDogDialog(): void {
    this._analytics.logButtonTap('dogProfile_removeDog');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'general-dialog',
      data: {
        title: 'Remove This Dog',
        content: 'Are you sure you want to remove this dog?',
        cancelText: 'No',
        okText: 'Remove',
        okFn: () => {
          dialogRef.close();
          this.dogRemoved.emit();
        },
      },
      autoFocus: false,
    });
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private updateData(): void {
    this.loadingIndicatorService.show();
    // <!-- CBW-784 Names -->
    const {
      firstName,
      lastName,
      dob,
      weight,
      breedArr,
      colorArr,
      gender,
      spayNeutered,
    } = this.form.value;
    this.updatePetGQL
      .mutate({
        petId: this.petId.toString(),
        petInput: {
          firstName,
          lastName,
          dateOfBirth: dob,
          weight: Math.round(this.convertUnit.transform(+weight, 'lb', 'g')),
          breeds: breedArr.map((breed, index) => ({
            breed: +breed.id,
            isPrimary: index === 0,
          })),
          colors: colorArr.map((color, index) => ({
            color: +color.id,
            isPrimary: index === 0,
          })),
          gender,
          spayNeutered,
        },
      })
      .pipe(
        map((res) => res.data.updatePet),
        finalize(() => this.loadingIndicatorService.hide())
      )
      .subscribe((pet) => {
        if (pet) {
          this.populateForms(pet);
          this.form.markAsPristine();
          this.editMode = false;
          this.petNameChange.emit(pet.firstName + ' ' + pet.lastName);
        }
      });
  }

  // filter breeds dropdown options

  private _filterBreeds(value: string | Breed): Breed[] {
    if (!value) {
      return this.breedList;
    }
    const filterValue =
      typeof value === 'string'
        ? value.toLowerCase()
        : value.label.toLowerCase();
    return this.breedList.filter((option) =>
      option.label.toLowerCase().includes(filterValue)
    );
  }

  private _manageFilteredBreedsByFormArrayIndex(index: number): void {
    this.filteredBreeds[index] = this.breedFormArray
      .at(index)
      .valueChanges.pipe(
        startWith(''),
        debounce(() => interval(250)),
        map((value) => this._filterBreeds(value))
      );
  }

  // filter colors dropdown options

  private _filterColors(value: string | Color): Color[] {
    if (!value) {
      return this.colorList;
    }
    const filterValue =
      typeof value === 'string'
        ? value.toLowerCase()
        : value.label.toLowerCase();
    return this.colorList.filter((option) =>
      option.label.toLowerCase().includes(filterValue)
    );
  }

  private _manageFilteredColorsByFormArrayIndex(index: number): void {
    this.filteredColors[index] = this.colorFormArray
      .at(index)
      .valueChanges.pipe(
        startWith(''),
        debounce(() => interval(250)),
        map((value) => this._filterColors(value))
      );
  }

  // popolate breedFormArray, called by populateForms()

  private populateBreeds(breeds: PetBreed[]): void {
    if (!breeds || breeds.length === 0) {
      this.addBreed();
    } else {
      this.breedFormArray.clear();

      // move primary breed to index 0
      const primaryIndex = breeds.findIndex((breed) => breed.isPrimary);
      const primaryBreed = breeds[primaryIndex];
      breeds.splice(primaryIndex, 1);
      breeds.splice(0, 0, primaryBreed);

      breeds.forEach((breed, index) => {
        const { id, label } = breed.breed;
        this.breedFormArray.push(
          this.fb.control({ id, label }, Validators.required)
        );
        this._manageFilteredBreedsByFormArrayIndex(index);
      });
    }
  }

  // popolate colorFormArray, called by populateForms()

  private populateColors(colors: PetColor[]): void {
    if (!colors || colors.length === 0) {
      this.addColor();
    } else {
      this.colorFormArray.clear();

      // move primary color to index 0
      const primaryIndex = colors.findIndex((color) => color.isPrimary);
      const primaryColor = colors[primaryIndex];
      colors.splice(primaryIndex, 1);
      colors.splice(0, 0, primaryColor);

      colors.forEach((color, index) => {
        const { id, label } = color.color;
        this.colorFormArray.push(
          this.fb.control({ id, label }, Validators.required)
        );
        this._manageFilteredColorsByFormArrayIndex(index);
      });
    }
  }

  filterBreedsOption() {
    for (let i = 0; i < this.filteredBreeds.length; i++) {
      this._manageFilteredBreedsByFormArrayIndex(i);
    }
  }

  filterColorOption() {
    for (let i = 0; i < this.filteredColors.length; i++) {
      this._manageFilteredColorsByFormArrayIndex(i);
    }
  }
}
